
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

const navLinks = [{
  name: 'Accueil',
  path: '/'
}, {
  name: 'Services',
  path: '/services'
}, {
  name: 'Galerie',
  path: '/gallery'
}, {
  name: 'À Propos',
  path: '/about'
}, {
  name: 'Contact',
  path: '/contact'
}];

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleScroll = () => {
      // Simple boolean check for scroll position
      setHasScrolled(window.scrollY > 20);
    };

    // Use passive event listener for better performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial calculation
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <header className={cn(
      "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
      hasScrolled ? "bg-brand-green-dark py-2 shadow-lg" : "bg-brand-green py-3 md:py-6"
    )}>
      <div className="container mx-auto px-4 md:px-8 max-w-6xl flex items-center justify-between">
        {/* Logo */}
        <div className="flex-shrink-0 w-1/4">
          <Link to="/" className="flex items-center">
            <img 
              src="/lovable-uploads/93ffdfe7-f204-4330-983d-c466933d9798.png" 
              alt="Entretiens McBain Logo" 
              className={cn(
                "transition-all duration-300",
                isMobile ? (hasScrolled ? "h-16" : "h-20") : (hasScrolled ? "h-20" : "h-24")
              )}
            />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center justify-center flex-grow">
          <div className="flex items-center space-x-10">
            {navLinks.map(link => (
              <Link 
                key={link.name} 
                to={link.path} 
                className="font-medium text-white hover:text-brand-stone-light transition-all duration-300 link-underline after:bg-brand-stone-light text-xl whitespace-nowrap" 
              >
                {link.name}
              </Link>
            ))}
          </div>
        </nav>

        {/* CTA Button */}
        <div className="hidden md:block flex-shrink-0 w-1/4 text-right">
          <Button href="/quote" size="lg" className="bg-brand-stone text-brand-green hover:bg-brand-stone-light transition-all duration-300 text-lg whitespace-nowrap">
            Obtenir un devis
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden">
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu" className="p-2 rounded-md text-white">
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-brand-green-dark shadow-md animate-fade-in z-50">
          <div className="container mx-auto px-4 max-w-6xl py-4">
            <div className="flex flex-col space-y-4">
              {navLinks.map(link => (
                <Link 
                  key={link.name} 
                  to={link.path} 
                  className="font-medium text-white py-3 px-2 hover:text-brand-stone-light hover:bg-brand-green/20 rounded-md transition-colors text-xl flex items-center" 
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-2">
                <Button 
                  href="/quote" 
                  size="lg" 
                  fullWidth 
                  className="bg-brand-stone text-brand-green hover:bg-brand-stone-light transition-all duration-300 text-lg py-6 mt-2"
                >
                  Obtenir un devis
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
